import { useEffect, useRef, useState } from "react";

/**
 * "Suggest-insert" payload — copy that should be programmatically
 * inserted into the composer next time it mounts/re-renders.
 *
 * Versioning lets the consumer detect a duplicate suggestion (same
 * text twice in a row) as a fresh write rather than a no-op.
 */
export interface SuggestInsert {
  text: string;
  version: number;
}

/**
 * Owns the composer's "suggest-insert" plumbing for `MessageArea`.
 *
 * `suggestInsert` is the pending text + version pair that
 * `MessageInput` reads as `externalInsert`. It's set by a handful of
 * `terminal:*` window events (snapshot, send-to-composer, etc.) via
 * `useTerminalEventListeners`. The version ref is bumped per write
 * so the consumer can dedupe.
 *
 * The effect clears any pending suggest the moment the active
 * channel changes — a snapshot meant for the channel you just left
 * shouldn't materialize as text in the channel you just entered.
 */
export function useSuggestInsert(channelId: string | null) {
  const [suggestInsert, setSuggestInsert] = useState<SuggestInsert | null>(
    null,
  );
  // Clear pending composer insert when switching channels — the
  // alternative (deriving from a version key) loses the user's local
  // edits in the destination channel.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSuggestInsert(null);
  }, [channelId]);
  const suggestInsertVersionRef = useRef(0);

  return { suggestInsert, setSuggestInsert, suggestInsertVersionRef };
}
